import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Note: bwVariationVersionSlice has been removed - functionality consolidated into bookWorksheetDataSlice
// TODO: Migrate remaining functionality to zone-based architecture
// import * as bwVariationVersionSlice from '../slices/bwVariationVersionSlice';

// Import from the new zone-based slice:
import {
  fetchVariations as bwdsFetchVariations,
  fetchVariationCounts as bwdsFetchVariationCounts
} from '../slices/bookWorksheetDataSlice';

// Import selectors from the new zone-based slice for data access
import {
  selectVariations,
  selectBooks,
  selectCharacters,
  selectLitGroups,
  selectCurrentLitGroup,
  selectCurrentBook,
  selectCurrentCharacter,
  selectCurrentVariation,
  selectVariationCounts
} from '../selectors/zoneSelectors';

/**
 * HYBRID Hook that combines bwVariationVersionSlice (edit state) with bookWorksheetSlice (data)
 * This provides a clean interface that uses the new specialized slice for variation/version editing
 * while maintaining access to all the data from the original slice
 * 
 * @returns {Object} Complete interface for variation/version management
 */
export const useBwVariationVersion = () => {
  const dispatch = useDispatch();

  // ===== DATA FROM NEW ZONE-BASED SLICE =====
  // Get entity data from the new zone-based slice
  const variations = useSelector(selectVariations);
  const variationCounts = useSelector(selectVariationCounts);
  const variationCountsStatus = null; // Not available in new slice yet
  const books = useSelector(selectBooks);
  const characters = useSelector(selectCharacters);
  const litGroups = useSelector(selectLitGroups);

  // Get selected entities from new zone-based slice
  const selectedLitGroup = useSelector(selectCurrentLitGroup);
  const selectedBook = useSelector(selectCurrentBook);
  const selectedCharacter = useSelector(selectCurrentCharacter);
  const selectedVariation = useSelector(selectCurrentVariation);

  // ===== EDIT STATE FROM NEW ZONE-BASED SLICE =====
  // Get edit state from the new zone-based slice
  const editVersion = useSelector(state => state.user.bookWorksheetData?.variationZone?.activeVersion || null);
  const editVersionData = useSelector(state => state.user.bookWorksheetData?.variationZone?.samples || []);
  const editMode = useSelector(state => state.user.bookWorksheetData?.variationZone?.editMode || 'view');
  const currentAction = null; // Not available in new slice yet
  const isFormDirty = useSelector(state => state.user.bookWorksheetData?.variationZone?.hasUnsavedChanges || false);
  const hasUnsavedChanges = useSelector(state => state.user.bookWorksheetData?.variationZone?.hasUnsavedChanges || false);
  const isSubmitting = useSelector(state => state.user.bookWorksheetData?.variationZone?.isSubmitting || false);
  const formValues = null; // Not available in new slice yet
  const editLoadingState = useSelector(state => state.user.bookWorksheetData?.variationZone?.loading || false);
  const editError = useSelector(state => state.user.bookWorksheetData?.variationZone?.error || null);
  const editErrorType = null; // Not available in new slice yet
  const showErrorHint = false; // Not available in new slice yet
  const errorHintMessage = null; // Not available in new slice yet
  const repositoryVersion = null; // Not available in new slice yet
  const lastAction = null; // Not available in new slice yet
  const editLastUpdated = useSelector(state => state.user.bookWorksheetData?.variationZone?.lastUpdated || null);
  const canSave = !isSubmitting && hasUnsavedChanges;
  const canCancel = hasUnsavedChanges;
  const isLoading = useSelector(state => state.user.bookWorksheetData?.variationZone?.loading || false);

  // Get selections from new zone-based slice
  const selections = useSelector(state => state.user.bookWorksheetData?.sharedData?.selections || {});
  const persistSelections = true; // Default to true for new slice

  // ===== RELATIONSHIP SELECTORS =====
  // Export selector functions for components to use directly with useSelector
  const relationshipSelectors = {
    // Note: These selectors are not available in the new zone-based architecture
    // Components should use the zone-based hooks instead
  };

  // ===== PASS-THROUGH THUNK FUNCTIONS =====
  // Data fetching functions (pass-through to new zone-based slice)
  const fetchVariations = useCallback((litGroupIdOrParams, bookId, characterId, forceRefresh) => {
    // Handle both old calling pattern and new object parameter pattern
    let params;
    if (typeof litGroupIdOrParams === 'object') {
      // New object parameter format
      params = litGroupIdOrParams;
    } else {
      // Legacy positional parameters or single characterId
      if (bookId === undefined && characterId === undefined && forceRefresh === undefined) {
        // Single characterId (legacy)
        params = { characterId: litGroupIdOrParams };
      } else {
        // Multiple positional parameters
        params = {
          litGroupId: litGroupIdOrParams,
          bookId,
          characterId,
          forceRefresh
        };
      }
    }

    console.log(`[BW_VAR_VER_HOOK] Fetching variations with params:`, params);
    return dispatch(bwdsFetchVariations(params));
  }, [dispatch]);

  const fetchVariationCounts = useCallback((litGroupIdOrParams, bookId, characterId, forceRefresh) => {
    // Handle both old calling pattern and new object parameter pattern
    let params;
    if (typeof litGroupIdOrParams === 'object') {
      // New object parameter format
      params = litGroupIdOrParams;
    } else {
      // Legacy positional parameters or single characterId
      if (bookId === undefined && characterId === undefined && forceRefresh === undefined) {
        // Single characterId (legacy)
        params = { characterId: litGroupIdOrParams };
      } else {
        // Multiple positional parameters
        params = {
          litGroupId: litGroupIdOrParams,
          bookId,
          characterId,
          forceRefresh
        };
      }
    }

    console.log(`[BW_VAR_VER_HOOK] Fetching variation counts with params:`, params);
    return dispatch(bwdsFetchVariationCounts(params));
  }, [dispatch]);

  const fetchCharacters = useCallback((bookId) => {
    console.log(`[BW_VAR_VER_HOOK] Fetching characters for book ${bookId} - TODO: Implement in zone-based architecture`);
    return Promise.resolve(); // Placeholder
  }, []);

  const fetchBooks = useCallback((litGroupId) => {
    console.log(`[BW_VAR_VER_HOOK] Fetching books for litGroup ${litGroupId} - TODO: Implement in zone-based architecture`);
    return Promise.resolve(); // Placeholder
  }, []);

  const fetchLitGroups = useCallback(() => {
    console.log(`[BW_VAR_VER_HOOK] Fetching literary groups - TODO: Implement in zone-based architecture`);
    return Promise.resolve(); // Placeholder
  }, []);

  // ===== CRUD OPERATIONS =====
  // Edit-aware CRUD operations - TODO: Implement in zone-based architecture
  const createVariation = useCallback((data) => {
    console.log(`[BW_VAR_VER_HOOK] Creating variation:`, data, '- TODO: Implement in zone-based architecture');
    return Promise.resolve(); // Placeholder
  }, []);

  const updateVariation = useCallback((data) => {
    console.log(`[BW_VAR_VER_HOOK] Updating variation:`, data, '- TODO: Implement in zone-based architecture');
    return Promise.resolve(); // Placeholder
  }, []);

  const deleteVariation = useCallback((data) => {
    console.log(`[BW_VAR_VER_HOOK] Deleting variation:`, data, '- TODO: Implement in zone-based architecture');
    return Promise.resolve(); // Placeholder
  }, []);

  // ===== EDIT-SPECIFIC FUNCTIONS =====
  // Edit version management - TODO: Implement in zone-based architecture
  const loadVariationForEdit = useCallback((params) => {
    console.log(`[BW_VAR_VER_HOOK] Loading variation for edit:`, params, '- TODO: Implement');
    return Promise.resolve(); // Placeholder
  }, []);

  const loadDefaultVersion = useCallback((params) => {
    console.log(`[BW_VAR_VER_HOOK] Loading default version:`, params, '- TODO: Implement');
    return Promise.resolve(); // Placeholder
  }, []);

  const saveVariation = useCallback((params) => {
    console.log(`[BW_VAR_VER_HOOK] Saving variation:`, params, '- TODO: Implement');
    return Promise.resolve(); // Placeholder
  }, []);

  const loadVersionForEdit = useCallback((params) => {
    console.log(`[BW_VAR_VER_HOOK] Loading version for edit:`, params, '- TODO: Implement');
    return Promise.resolve(); // Placeholder
  }, []);

  const loadVersionForClone = useCallback((params) => {
    console.log(`[BW_VAR_VER_HOOK] Loading version for clone:`, params, '- TODO: Implement');
    return Promise.resolve(); // Placeholder
  }, []);

  const deleteVersion = useCallback((params) => {
    console.log(`[BW_VAR_VER_HOOK] Deleting version:`, params, '- TODO: Implement');
    return Promise.resolve(); // Placeholder
  }, []);

  // ===== SELECTION MANAGEMENT =====
  // Selection functions - TODO: Implement in zone-based architecture
  const setSelection = useCallback((level, id) => {
    console.log(`[BW_VAR_VER_HOOK] Setting selection: ${level} = ${id} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const clearSelection = useCallback((level) => {
    console.log(`[BW_VAR_VER_HOOK] Clearing selection: ${level} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const setPersistSelections = useCallback((persist) => {
    console.log(`[BW_VAR_VER_HOOK] Set persist selections: ${persist} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  // ===== FORM STATE MANAGEMENT =====
  // Form state functions - TODO: Implement in zone-based architecture
  const updateFormField = useCallback((field, value) => {
    console.log(`[BW_VAR_VER_HOOK] Update form field: ${field} = ${value} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const setFormDirty = useCallback((dirty) => {
    console.log(`[BW_VAR_VER_HOOK] Set form dirty: ${dirty} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const setFormSubmitting = useCallback((submitting) => {
    console.log(`[BW_VAR_VER_HOOK] Set form submitting: ${submitting} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const updateVersionField = useCallback((field, value) => {
    console.log(`[BW_VAR_VER_HOOK] Update version field: ${field} = ${value} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const updateActiveSample = useCallback((sample) => {
    console.log(`[BW_VAR_VER_HOOK] Update active sample:`, sample, '- TODO: Implement');
    return Promise.resolve(); // Placeholder
  }, []);

  // ===== ERROR MANAGEMENT =====
  // Error handling functions - TODO: Implement in zone-based architecture
  const setEditError = useCallback((error) => {
    console.log(`[BW_VAR_VER_HOOK] Set edit error:`, error, '- TODO: Implement');
    return Promise.resolve(); // Placeholder
  }, []);

  const clearEditError = useCallback(() => {
    console.log(`[BW_VAR_VER_HOOK] Clear edit error - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const showErrorHintAction = useCallback((message) => {
    console.log(`[BW_VAR_VER_HOOK] Show error hint: ${message} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const clearEditForm = useCallback(() => {
    console.log(`[BW_VAR_VER_HOOK] Clear edit form - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  // ===== MODE MANAGEMENT =====
  // Edit mode functions - TODO: Implement in zone-based architecture
  const setEditMode = useCallback((mode) => {
    console.log(`[BW_VAR_VER_HOOK] Set edit mode: ${mode} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const setCurrentAction = useCallback((action) => {
    console.log(`[BW_VAR_VER_HOOK] Set current action: ${action} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const setRepositoryVersion = useCallback((version) => {
    console.log(`[BW_VAR_VER_HOOK] Set repository version: ${version} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  const setEditLoadingState = useCallback((type, status) => {
    console.log(`[BW_VAR_VER_HOOK] Set edit loading state: ${type} = ${status} - TODO: Implement`);
    return Promise.resolve(); // Placeholder
  }, []);

  // ===== RETURN UNIFIED INTERFACE =====
  return {
    // ===== DATA (from original bookWorksheetSlice) =====
    variations,
    variationCounts,
    variationCountsStatus,
    books,
    characters,
    litGroups,
    selectedLitGroup,
    selectedBook,
    selectedCharacter,
    selectedVariation,

    // ===== EDIT STATE (from bwVariationVersionSlice) =====
    editVersion,
    editVersionData,
    editMode,
    currentAction,
    isFormDirty,
    hasUnsavedChanges,
    isSubmitting,
    formValues,
    editLoadingState,
    editError,
    editErrorType,
    showErrorHint,  // Boolean state value
    errorHintMessage,
    repositoryVersion,
    lastAction,
    editLastUpdated,
    canSave,
    canCancel,
    isLoading,
    selections,
    persistSelections,

    // ===== RELATIONSHIP SELECTORS =====
    relationshipSelectors,

    // ===== DATA FETCHING FUNCTIONS =====
    fetchVariations,
    fetchVariationCounts,
    fetchCharacters,
    fetchBooks,
    fetchLitGroups,

    // ===== CRUD OPERATIONS =====
    createVariation,
    updateVariation,
    deleteVariation,

    // ===== EDIT-SPECIFIC FUNCTIONS =====
    loadVariationForEdit,
    loadDefaultVersion,
    saveVariation,
    loadVersionForEdit,
    loadVersionForClone,
    deleteVersion,

    // ===== SELECTION MANAGEMENT =====
    setSelection,
    clearSelection,
    setPersistSelections,

    // ===== FORM STATE MANAGEMENT =====
    updateFormField,
    setFormDirty,
    setFormSubmitting,
    updateVersionField,
    updateActiveSample,

    // ===== ERROR MANAGEMENT =====
    setEditError,
    clearEditError,
    showErrorHintAction,  // Function for showing error hints
    clearEditForm,

    // ===== MODE MANAGEMENT =====
    setEditMode,
    setCurrentAction,
    setRepositoryVersion,
    setEditLoadingState
  };
};

export default useBwVariationVersion;
